import { useState } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { cn } from '@/lib/utils'
import type { RouterOutput } from '@/config/trpc'

type Snapshot = RouterOutput['analytics']['postDetail']['snapshots'][number]

const metrics = [
  { key: 'views', label: 'Views', color: 'var(--primary)' },
  { key: 'likes', label: 'Likes', color: 'hsl(0, 84%, 60%)' },
  { key: 'comments', label: 'Comments', color: 'hsl(142, 71%, 45%)' },
  { key: 'shares', label: 'Shares', color: 'hsl(262, 83%, 58%)' },
  { key: 'saves', label: 'Saves', color: 'hsl(38, 92%, 50%)' },
  { key: 'engagementRate', label: 'Eng. Rate', color: 'hsl(187, 92%, 35%)' },
] as const

type MetricKey = (typeof metrics)[number]['key']

export function MetricChart({ snapshots }: { snapshots: Snapshot[] }) {
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('views')

  const metric = metrics.find((m) => m.key === selectedMetric)!

  const data = snapshots.map((s) => ({
    date: new Date(s.snapshotAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    }),
    value:
      selectedMetric === 'engagementRate'
        ? s.engagementRate != null
          ? +(s.engagementRate * 100).toFixed(2)
          : null
        : (s[selectedMetric] as number | null),
  }))

  if (snapshots.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="text-muted-foreground/40">
            <path
              d="M2 14l4-5 3 3 5-7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-muted-foreground text-sm">No metric data yet</p>
        <p className="text-muted-foreground/50 text-xs">
          Metrics will appear after the first sync.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-1.5">
        {metrics.map((m) => (
          <button
            key={m.key}
            onClick={() => setSelectedMetric(m.key)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-all',
              selectedMetric === m.key
                ? 'text-primary-foreground shadow-sm'
                : 'border-border bg-background text-foreground hover:bg-hover border',
            )}
            style={selectedMetric === m.key ? { backgroundColor: m.color } : undefined}>
            {m.label}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="metricGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={metric.color} stopOpacity={0.15} />
              <stop offset="100%" stopColor={metric.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--border)"
            strokeOpacity={0.5}
          />
          <XAxis
            dataKey="date"
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={45}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--popover-foreground)',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
            formatter={(value) => [
              selectedMetric === 'engagementRate' ? `${value}%` : String(value ?? '-'),
              metric.label,
            ]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={metric.color}
            strokeWidth={2}
            fill="url(#metricGradient)"
            dot={{ r: 3, fill: metric.color, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: metric.color, strokeWidth: 2, stroke: 'var(--card)' }}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
